/**
 * ⭐⭐ **O TÓPICO VEÍCULOS NO DESENHO DE IMÓVEIS — 18/09/2026.**
 *
 * > *"O layout e design de IMÓVEIS ser a referência para replicar nas outras
 * > abas… cada aba tem as suas características e funções, então, estou me
 * > referindo a parte visual. Isso servirá mais para padronizar o HUB inteiro."*
 * > — Richard
 *
 * **Antes:** uma seção recolhível por parte, cada uma com as três espécies e
 * os textos de fonte repetidos — 2.600 px com três partes. **Agora:** as peças
 * de Imóveis — resumo no alto, uma parte por vez, o botão de lançar, a faixa do
 * que foi pesquisado para a parte, uma linha por veículo que abre o detalhe.
 *
 * ⛔ **Muda aparência e lugar, nunca o que o botão faz.** As duas buscas
 * (`BuscarVeiculos`, paga e só do master; `BuscarAeronaves`, grátis),
 * `FichaDoAtivo`, `FormularioDeOnus`, o aceite, corrigir e apagar são as mesmas
 * peças de antes — importadas, não copiadas.
 *
 * ⚠️ **As três espécies continuam ditas mesmo vazias** — "não pesquisamos" não
 * pode parecer "não tem". Mudaram de lugar: saíram dos três blocos repetidos
 * por parte e viraram as marcas da faixa "Pesquisado para esta parte", com o
 * estado que a trilha da apuração já calcula (feito, nada consta, sem caminho…).
 */
import { useEffect, useState } from 'react';
import {
  apiInvestigados,
  apiTrilha,
  QUALIFICACOES,
  type AtivoDoInvestigado,
  type ClassificacaoDoAtivo,
  type EspecieDeVeiculo,
  type GravamesDoTopico,
  type Investigado,
  type LinhaDaTrilha,
} from '../lib/api';
import { usarEhMaster } from '../lib/papel';
import { avisar, avisarErro } from './Avisos';
import { Confirmar } from './Confirmar';
import {
  BuscarAeronaves,
  AprofundarVeiculos,
  BuscarVeiculos,
  DOCUMENTADA,
  FichaDoAtivo,
  FONTE_DA_ESPECIE,
  FormularioDeOnus,
  PEDE_VALOR,
  ROTULO,
  ROTULO_DA_MODALIDADE,
  dinheiro,
} from './AtivosLocalizados';
import { iniciais, Traco } from './ImoveisDoCaso';
import { dizer } from './TrilhaDaApuracao';
import '../estilos/imoveis-2026.css';

const ESPECIES: EspecieDeVeiculo[] = ['automovel', 'aeronave', 'embarcacao'];
const NOME: Record<EspecieDeVeiculo, { um: string; varios: string; trilha: string }> = {
  automovel: { um: 'Automóvel', varios: 'Automóveis', trilha: 'veiculos' },
  aeronave: { um: 'Aeronave', varios: 'Aeronaves', trilha: 'aeronaves' },
  embarcacao: { um: 'Embarcação', varios: 'Embarcações', trilha: 'embarcacoes' },
};

/** A situação na linha: o nome curto e o tom — os mesmos da ficha de lançar. */
const SITUACAO: Partial<Record<ClassificacaoDoAtivo, { curto: string; tom: string; ordem: number }>> = {
  veiculo_atual: { curto: 'Atual', tom: 'livre', ordem: 0 },
  veiculo_situacao_nao_apurada: { curto: 'Não apurada', tom: 'terceiro', ordem: 1 },
  veiculo_historico: { curto: 'Histórico', tom: 'transmitido', ordem: 2 },
  veiculo_baixado: { curto: 'Baixado', tom: 'encerrada', ordem: 3 },
};

const emData = (d: string | null | undefined) => (d ? d.split('-').reverse().join('/') : '');
/* Só a primeira letra: "Falta CPF" vira "falta CPF", e o Hub continua Hub. */
const minuscula = (t: string) => t.charAt(0).toLocaleLowerCase('pt-BR') + t.slice(1);
const ativoGravame = (o: { situacao: string | null }) => (o.situacao ?? '').trim().toUpperCase() === 'ATIVO';

type Dados = { ativos: AtivoDoInvestigado[]; gravames: GravamesDoTopico | null };
type Painel = null | 'automovel' | 'aeronave';

export function VeiculosDoCaso({ casoId, chave }: { casoId: string; chave: string }) {
  const ehMaster = usarEhMaster();
  const [partes, setPartes] = useState<Investigado[] | null>(null);
  const [porParte, setPorParte] = useState<Record<string, Dados>>({});
  const [trilha, setTrilha] = useState<Record<string, LinhaDaTrilha[]>>({});
  const [escolhida, setEscolhida] = useState<string | null>(null);
  const [revisao, setRevisao] = useState(0);
  const [painel, setPainel] = useState<Painel>(null);
  const [ficha, setFicha] = useState<null | { corrigindo: AtivoDoInvestigado | null }>(null);

  useEffect(() => {
    let vivo = true;
    void apiInvestigados
      .listar(casoId)
      .then((i) => {
        if (!vivo) return;
        setPartes(i.investigados);
        setEscolhida((atual) => atual ?? i.investigados[0]?.id ?? null);
      })
      .catch(() => vivo && setPartes([]));
    return () => {
      vivo = false;
    };
  }, [casoId, chave]);

  useEffect(() => {
    if (!partes) return;
    let vivo = true;
    void Promise.all(
      partes.map((p) =>
        apiInvestigados
          .ativos(p.id)
          .then(
            (r) =>
              [p.id, { ativos: r.ativos.filter((a) => a.categoria === 'veiculos'), gravames: r.porCategoria?.veiculos ?? null }] as const,
          )
          .catch(() => [p.id, { ativos: [], gravames: null }] as const),
      ),
    ).then((pares) => {
      if (vivo) setPorParte(Object.fromEntries(pares));
    });
    /* A faixa lê o estado que a trilha já calcula — só leitura, nada consulta. */
    void apiTrilha
      .doCaso(casoId)
      .then((t) => vivo && setTrilha(Object.fromEntries(t.partes.map((p) => [p.investigadoId, p.linhas]))))
      .catch(() => vivo && setTrilha({}));
    return () => {
      vivo = false;
    };
  }, [partes, revisao, casoId]);

  if (partes === null) return <p className="ajuda">Carregando…</p>;
  if (partes.length === 0) {
    return (
      <p className="ajuda ajuda-solta">
        Este caso ainda não tem parte investigada cadastrada. Os veículos se lançam por pessoa — cadastre os
        investigados no caso primeiro.
      </p>
    );
  }

  const parte = partes.find((p) => p.id === escolhida) ?? partes[0]!;
  const dados = porParte[parte.id];
  const todos = Object.values(porParte).flatMap((d) => d.ativos);
  const aoMudar = () => setRevisao((n) => n + 1);

  return (
    <div className="imv">
      <Resumo ativos={todos} />

      <div className="imv-pessoas" role="tablist" aria-label="Parte investigada">
        {partes.map((p) => {
          const n = porParte[p.id]?.ativos.length ?? 0;
          return (
            <button
              key={p.id}
              type="button"
              role="tab"
              aria-selected={p.id === parte.id}
              className={p.id === parte.id ? 'ativo' : undefined}
              onClick={() => {
                if (ficha) {
                  avisar('Lance ou cancele o formulário aberto antes de trocar de parte.');
                  return;
                }
                setEscolhida(p.id);
                setPainel(null);
              }}
            >
              <span className="imv-av">{iniciais(p.nome)}</span>
              <span className="imv-txt">
                <b>{p.nome}</b>
                <small>{QUALIFICACOES[p.qualificacao]}</small>
              </span>
              <span className={`imv-conta${n === 0 ? ' zero' : ''}`} title={n === 0 ? 'nada lançado' : undefined}>
                {n}
              </span>
            </button>
          );
        })}
      </div>

      {ficha ? (
        <FichaDoAtivo
          key={ficha.corrigindo?.id ?? 'novo'}
          investigado={parte}
          categoria="veiculos"
          corrigindo={ficha.corrigindo}
          aoFechar={() => setFicha(null)}
          aoGravar={() => {
            setFicha(null);
            aoMudar();
          }}
        />
      ) : (
        <>
          <div className="imv-acoes">
            <button
              type="button"
              className="imv-bt primario"
              onClick={() => {
                setPainel(null);
                setFicha({ corrigindo: null });
              }}
            >
              <Traco d={['M12 5v14', 'M5 12h14']} />
              Lançar veículo
            </button>
          </div>

          <div className="imv-fontes">
            <span className="imv-rot">Pesquisado para esta parte</span>
            {ESPECIES.map((e) => {
              const l = (trilha[parte.id] ?? []).find((x) => x.chave === NOME[e].trilha);
              const ok = l?.palavra === 'feito' || l?.palavra === 'nada_consta';
              return (
                <span key={e} className={`imv-fonte${ok ? ' ok' : ' falta'}`} title={FONTE_DA_ESPECIE[e]}>
                  <i />
                  {NOME[e].varios} · {l ? minuscula(dizer(l)) : 'em branco'}
                </span>
              );
            })}
            <span className="imv-esp" />
            {/* ⚠️ Só master: R$ 15,41 por consulta — o botão de antes, no mesmo lugar da faixa. */}
            {ehMaster && (
              <button
                type="button"
                className={`imv-bt fantasma p${painel === 'automovel' ? ' aberto' : ''}`}
                aria-expanded={painel === 'automovel'}
                onClick={() => setPainel(painel === 'automovel' ? null : 'automovel')}
              >
                <Traco d={['M11 19a8 8 0 1 0 0-16 8 8 0 0 0 0 16', 'm21 21-4.35-4.35']} />
                Buscar automóveis
              </button>
            )}
            <button
              type="button"
              className={`imv-bt fantasma p${painel === 'aeronave' ? ' aberto' : ''}`}
              aria-expanded={painel === 'aeronave'}
              onClick={() => setPainel(painel === 'aeronave' ? null : 'aeronave')}
            >
              <Traco d={['M11 19a8 8 0 1 0 0-16 8 8 0 0 0 0 16', 'm21 21-4.35-4.35']} />
              Buscar aeronaves
            </button>
          </div>

          {painel === 'automovel' && ehMaster && (
            <div className="imv-painel">
              <p className="imv-painel-titulo">Automóveis · {parte.nome}</p>
              <p className="ajuda">{FONTE_DA_ESPECIE.automovel}</p>
              <BuscarVeiculos investigadoId={parte.id} aoLancar={aoMudar} />
              {/*
                ⭐ **O aprofundamento vem DEPOIS da busca, e é de propósito.**
                A busca diz quais veículos existem; o aprofundamento diz se
                valem penhora. Antes dela não há o que aprofundar, e o
                componente se esconde sozinho quando a conta é zero.
              */}
              <AprofundarVeiculos
                investigadoId={parte.id}
                quantos={dados?.ativos.length ?? 0}
                aoLancar={aoMudar}
              />
            </div>
          )}
          {painel === 'aeronave' && (
            <div className="imv-painel">
              <p className="imv-painel-titulo">Aeronaves · {parte.nome}</p>
              <p className="ajuda">{FONTE_DA_ESPECIE.aeronave}</p>
              <BuscarAeronaves investigadoId={parte.id} aoLancar={aoMudar} />
            </div>
          )}

          <ListaDeVeiculos
            dados={dados}
            investigadoId={parte.id}
            ehMaster={ehMaster}
            aoMudar={aoMudar}
            aoCorrigir={(a) => {
              setPainel(null);
              setFicha({ corrigindo: a });
            }}
          />
        </>
      )}
    </div>
  );
}

/* ── O resumo do caso ─────────────────────────────────────────────────────── */
function Resumo({ ativos }: { ativos: AtivoDoInvestigado[] }) {
  const atuais = ativos.filter((a) => a.classificacao === 'veiculo_atual');
  const com = atuais.filter((a) => a.valorAtribuivel !== null);
  const somaAtuais = com.length === 0 ? null : com.reduce((s, a) => s + (a.valorAtribuivel ?? 0), 0);
  const comGravame = atuais.filter((a) => a.onus.some(ativoGravame));
  const gravames = comGravame.reduce((n, a) => n + a.onus.filter(ativoGravame).length, 0);
  const fora = ativos.filter((a) => !a.entraNaConsolidacao).length;
  const semValor = ativos.filter((a) => PEDE_VALOR.includes(a.classificacao) && a.valorAtribuivel === null).length;
  const plural = (n: number, um: string, varios: string) => `${n} ${n === 1 ? um : varios}`;
  return (
    <div className="imv-resumo">
      <div className="imv-kpi k-livre">
        <span className="imv-rot">Atuais</span>
        <strong>{somaAtuais === null ? '—' : dinheiro(somaAtuais)}</strong>
        <small>{plural(atuais.length, 'veículo no patrimônio', 'veículos no patrimônio')}</small>
      </div>
      <div className="imv-kpi k-onus">
        <span className="imv-rot">Com gravame</span>
        <strong>{comGravame.length}</strong>
        <small>dos atuais · {plural(gravames, 'gravame ativo', 'gravames ativos')}</small>
      </div>
      <div className="imv-kpi k-fora">
        <span className="imv-rot">Fora do atual</span>
        <strong>{fora}</strong>
        <small>não soma ao patrimônio</small>
      </div>
      <div className="imv-kpi k-sem">
        <span className="imv-rot">Sem avaliação</span>
        <strong>{semValor}</strong>
        <small>não apurado — não é zero</small>
      </div>
    </div>
  );
}

/* ── A lista ──────────────────────────────────────────────────────────────── */
function ListaDeVeiculos({
  dados,
  investigadoId,
  ehMaster,
  aoMudar,
  aoCorrigir,
}: {
  dados: Dados | undefined;
  investigadoId: string;
  ehMaster: boolean;
  aoMudar: () => void;
  aoCorrigir: (a: AtivoDoInvestigado) => void;
}) {
  const [aberto, setAberto] = useState<string | null>(null);
  if (!dados) return <p className="ajuda">Carregando…</p>;
  const g = dados.gravames;
  /* A ordem: a espécie, e dentro dela o que é dele hoje antes do que saiu. */
  const ordem = [...dados.ativos].sort(
    (x, y) =>
      ESPECIES.indexOf(x.especie ?? 'automovel') - ESPECIES.indexOf(y.especie ?? 'automovel') ||
      (SITUACAO[x.classificacao]?.ordem ?? 9) - (SITUACAO[y.classificacao]?.ordem ?? 9),
  );
  return (
    <>
      <div className="imv-lista">
        <div className="imv-grade imv-cab">
          <span className="imv-rot">Situação</span>
          <span className="imv-rot">Veículo</span>
          <span className="imv-rot">Aquisição ou saída</span>
          <span className="imv-rot dir">Valor na conta</span>
          <span />
        </div>
        {ordem.length === 0 ? (
          /* ⚠️ Vazio diz "nada lançado" e NÃO some: ausência de bem não é ausência de pesquisa. */
          <p className="imv-vazio">Nada lançado para esta parte.</p>
        ) : (
          ordem.map((a) => (
            <LinhaDoVeiculo
              key={a.id}
              ativo={a}
              aberto={aberto === a.id}
              aoAbrir={() => setAberto(aberto === a.id ? null : a.id)}
              investigadoId={investigadoId}
              ehMaster={ehMaster}
              aoMudar={aoMudar}
              aoCorrigir={aoCorrigir}
            />
          ))
        )}
      </div>
      <div className="imv-legenda">
        <span>
          <b>—</b> valor não apurado: o bem conta na lista e fica fora da soma
        </span>
        {g && g.exposicaoQuantos > 0 && (
          <span>
            <span className="imv-info">i</span> Gravames ativos: {g.exposicaoQuantos}
            {g.exposicao > 0 && <> · somam {dinheiro(g.exposicao)}</>}
            {g.exposicaoSemValor > 0 && <> · {g.exposicaoSemValor} sem valor no papel</>} — valor do ato, não saldo
            devedor; <strong>não se subtraem</strong> do patrimônio
          </span>
        )}
        {g && g.gravamesSemSituacao > 0 && (
          <span>
            ⚠️ {g.gravamesSemSituacao} gravame(s) com <strong>situação não apurada</strong> — não somam e não se ignoram
          </span>
        )}
      </div>
    </>
  );
}

function LinhaDoVeiculo({
  ativo,
  aberto,
  aoAbrir,
  investigadoId,
  ehMaster,
  aoMudar,
  aoCorrigir,
}: {
  ativo: AtivoDoInvestigado;
  aberto: boolean;
  aoAbrir: () => void;
  investigadoId: string;
  ehMaster: boolean;
  aoMudar: () => void;
  aoCorrigir: (a: AtivoDoInvestigado) => void;
}) {
  const [aApagar, setAApagar] = useState(false);
  /* ⛔ Apagar um gravame pergunta antes — a regra dele, 18/09/2026. */
  const [gravameAApagar, setGravameAApagar] = useState<AtivoDoInvestigado['onus'][number] | null>(null);
  const gravamesAtivos = ativo.onus.filter(ativoGravame);
  const temFiduciaria = gravamesAtivos.some((o) => /aliena[cç][aã]o\s+fiduci|fiduci[aá]ri/i.test(o.natureza));
  const sit = SITUACAO[ativo.classificacao];
  const saiu = ativo.classificacao === 'veiculo_historico' || ativo.classificacao === 'veiculo_baixado';
  const movimento = [
    emData(ativo.ocorridoEm) || null,
    ativo.transmitente && !saiu ? `de ${ativo.transmitente}` : null,
    ativo.adquirente && saiu ? `para ${ativo.adquirente}${ativo.vinculoEntrePartes ? ` (${ativo.vinculoEntrePartes.toLocaleLowerCase('pt-BR')})` : ''}` : null,
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <article className={`imv-linha${aberto ? ' aberta' : ''}`}>
      <button type="button" className="imv-grade imv-linha-topo" onClick={aoAbrir} aria-expanded={aberto}>
        <span className="imv-sit-coluna">
          <span className={`imv-sit ${sit?.tom ?? ''}`}>
            <i />
            {sit?.curto ?? ROTULO[ativo.classificacao]}
          </span>
          {gravamesAtivos.length > 0 && (
            <span className="imv-tag">
              {gravamesAtivos.length} gravame{gravamesAtivos.length === 1 ? '' : 's'} ativo{gravamesAtivos.length === 1 ? '' : 's'}
            </span>
          )}
          {!ativo.entraNaConsolidacao && <span className="imv-tag cinza">fora do patrimônio atual</span>}
        </span>
        <span className="imv-bem">
          <b>{ativo.descricao}</b>
          {ativo.identificacao && <span className="imv-mono">{ativo.identificacao}</span>}
          <small>{NOME[ativo.especie ?? 'automovel'].um}</small>
        </span>
        <span className="imv-ato">
          <b>{ativo.ocorridoEm ? (saiu ? 'Saída' : 'Aquisição') : '—'}</b>
          {movimento && <small>{movimento}</small>}
        </span>
        <span className="imv-valor">
          {ativo.valorAtribuivel !== null ? (
            <>
              <b>{dinheiro(ativo.valorAtribuivel)}</b>
              <small>
                {ativo.modalidadeDaAvaliacao ? ROTULO_DA_MODALIDADE[ativo.modalidadeDaAvaliacao].toLocaleLowerCase('pt-BR') : 'procedência não declarada'}
                {ativo.percentual !== null && ativo.percentual !== 100 ? ` · ${ativo.percentual}%` : ''}
                {!ativo.entraNaConsolidacao ? ' · não soma' : ''}
              </small>
            </>
          ) : (
            <>
              <b className="na">—</b>
              <small>não apurado</small>
            </>
          )}
        </span>
        <span className="imv-chev" aria-hidden="true">
          <Traco d={[aberto ? 'm6 15 6-6 6 6' : 'm6 9 6 6 6-6']} />
        </span>
      </button>

      {aberto && (
        <div className="imv-detalhe">
          <div>
            <h5 className="imv-rot">Avaliações</h5>
            {(ativo.avaliacoes ?? []).length === 0 ? (
              <p className="imv-nada">Sem avaliação — o bem conta sem somar dinheiro. Não é zero.</p>
            ) : (
              <ul className="imv-mini">
                {ativo.avaliacoes.map((x) => (
                  <li key={x.id} className={x.entraNaConta ? 'conta' : undefined}>
                    <b>{dinheiro(x.valor)}</b>
                    <span>
                      {x.modalidade ? ROTULO_DA_MODALIDADE[x.modalidade] : 'procedência não declarada'}
                      {x.modalidade && !DOCUMENTADA[x.modalidade] && x.modalidade !== 'a_apurar' ? ' (derivada)' : ''}
                      {x.referencia ? ` · ${x.referencia}` : ''}
                      {x.avaliadaEm ? ` · ${emData(x.avaliadaEm)}` : ''}
                    </span>
                    {x.entraNaConta && <em>na conta</em>}
                  </li>
                ))}
              </ul>
            )}
            {ativo.valorDeclarado !== null && (
              <p className="imv-nada">
                Declarado no ato <b className="imv-mono">{dinheiro(ativo.valorDeclarado)}</b> — o preço do registro, não
                é avaliação.
              </p>
            )}
          </div>

          <div>
            <h5 className="imv-rot">Gravames</h5>
            {ativo.onus.length === 0 ? (
              <p className="imv-nada">Nenhum lançado.</p>
            ) : (
              <ul className="imv-mini">
                {ativo.onus.map((o) => (
                  <li key={o.id}>
                    <b>{o.valor !== null ? dinheiro(o.valor) : '—'}</b>
                    <span>
                      {o.natureza}
                      {o.credor ? ` · ${o.credor}` : ''}
                      {o.constituidoEm ? ` · desde ${emData(o.constituidoEm)}` : ''}
                    </span>
                    <em className={o.situacao ? 'sit' : 'sit na'}>{o.situacao ?? 'não apurada'}</em>
                    {ehMaster && (
                      <button type="button" className="botao-texto" onClick={() => setGravameAApagar(o)}>
                        apagar
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            )}
            {ehMaster && <FormularioDeOnus ativo={ativo} investigadoId={investigadoId} aoLancar={aoMudar} />}
          </div>

          <div>
            <h5 className="imv-rot">Registro</h5>
            <ul className="imv-mini">
              <li>
                <span>{NOME[ativo.especie ?? 'automovel'].um}{ativo.identificacao ? ` · ${ativo.identificacao}` : ''}</span>
              </li>
              {ativo.percentual !== null && (
                <li>
                  <span>Participação {ativo.percentual}%</span>
                </li>
              )}
              {ativo.transmitente && (
                <li>
                  <span>De {ativo.transmitente}</span>
                </li>
              )}
              {ativo.adquirente && (
                <li>
                  <span>
                    Para {ativo.adquirente}
                    {ativo.vinculoEntrePartes ? ` (${ativo.vinculoEntrePartes})` : ''}
                  </span>
                </li>
              )}
              {ativo.proprietarioAtual && (
                <li>
                  <span>Hoje em nome de {ativo.proprietarioAtual}</span>
                </li>
              )}
              {ativo.classificacao === 'veiculo_situacao_nao_apurada' && (
                <li>
                  <span>A fonte não disse se ainda é dele: não soma até a situação ser confirmada.</span>
                </li>
              )}
            </ul>
          </div>

          {temFiduciaria && (
            <div className="imv-travas">
              <p>
                ⚠️ <strong>Alienação fiduciária ativa.</strong> A propriedade resolúvel é do credor fiduciário: o
                investigado é <strong>titular dos direitos aquisitivos</strong> — é o direito que se alcança, não o veículo.
              </p>
            </div>
          )}

          {ativo.interpretacao && (
            <div className={`imv-interpretacao${ativo.aceita ? ' aceita' : ''}`}>
              <p>{ativo.interpretacao}</p>
              <p className="ajuda">
                {ativo.aceita ? 'Aceita — entra no documento entregue.' : 'Escrita pelo Hub, ainda não revista. Não entra no documento entregue.'}
                {ehMaster && (
                  <>
                    {' '}
                    <button
                      type="button"
                      className="botao-texto"
                      onClick={() =>
                        void (ativo.aceita
                          ? apiInvestigados.desfazerAceite(investigadoId, ativo.id)
                          : apiInvestigados.aceitarAtivo(investigadoId, ativo.id)
                        )
                          .then(() => {
                            aoMudar();
                            avisar(ativo.aceita ? 'Aceite desfeito.' : 'Aceita — entra no documento entregue.');
                          })
                          .catch(avisarErro)
                      }
                    >
                      {ativo.aceita ? 'desfazer o aceite' : 'aceitar'}
                    </button>
                  </>
                )}
              </p>
            </div>
          )}

          {ehMaster && (
            <div className="imv-detalhe-acoes">
              <button type="button" className="imv-bt p" onClick={() => aoCorrigir(ativo)}>
                <Traco d={['M4 20h4L19 9l-4-4L4 16z', 'M13.5 6.5l4 4']} />
                Corrigir
              </button>
              <button type="button" className="imv-bt p fantasma perigo" onClick={() => setAApagar(true)}>
                <Traco d={['M4 7h16', 'M9 7V4.5h6V7', 'M6.5 7l1 13h9l1-13']} />
                Apagar
              </button>
            </div>
          )}
        </div>
      )}

      {aApagar && (
        <Confirmar
          titulo="Apagar este lançamento?"
          descricao={
            ativo.origem
              ? `"${ativo.descricao}" veio de consulta. Apagá-lo não devolve o que a consulta custou.`
              : `"${ativo.descricao}" será apagado, com os gravames lançados nele.`
          }
          aoConfirmar={async () => {
            await apiInvestigados.apagarAtivo(investigadoId, ativo.id);
            setAApagar(false);
            aoMudar();
          }}
          aoFechar={() => setAApagar(false)}
        />
      )}
      {gravameAApagar && (
        <Confirmar
          titulo="Apagar este gravame?"
          descricao={`${gravameAApagar.natureza}${gravameAApagar.credor ? ` · ${gravameAApagar.credor}` : ''} sai deste veículo agora, e não volta.`}
          aoConfirmar={() =>
            void apiInvestigados
              .apagarOnus(investigadoId, ativo.id, gravameAApagar.id)
              .then(aoMudar)
              .catch(avisarErro)
          }
          aoFechar={() => setGravameAApagar(null)}
        />
      )}
    </article>
  );
}
